import { transformGetPathSweeps, transformPathLineInputPath } from 'src/service/navigation/data';
import {
  CombinedSweep,
  ITargetObjectData,
  NavigationType,
  RequestFunction,
} from 'src/service/navigation/types';

interface PathCache {
  originalPath: any[];
  modifiedPath: any[];
  hasTeleports: boolean;
}

export class PathFinder {
  private pathCache = new Map<string, PathCache>();

  constructor(private tourId: any, private requestFn: RequestFunction) {}

  private generateCacheKey(endSID: string, navigationType: NavigationType): string {
    return `${endSID}_${navigationType}`;
  }

  async createPath(
    pathStartSID: string,
    pathEndSID: string,
    pathCalculationSweeps: Map<string, CombinedSweep>,
    targetObjectData?: ITargetObjectData,
    navigationType: NavigationType = NavigationType.Shortest,
  ): Promise<{ path: any; hasTeleports: any }> {
    const cacheKey = this.generateCacheKey(pathEndSID, navigationType);
    const cachedPath = this.pathCache.get(cacheKey);

    // Check if we can reuse existing path
    if (cachedPath) {
      const pathSIDs = cachedPath.originalPath.map((p) => p.data?.id);
      const startIndex = pathSIDs.indexOf(pathStartSID);
      const endIndex = pathSIDs.indexOf(pathEndSID);

      // If start point exists in the original cached path
      if (startIndex !== -1 && endIndex !== -1) {

        // Create modified path from startIndex to end
        const modifiedPath = [...cachedPath.originalPath.slice(startIndex)];

        // Update cache with modified path
        this.pathCache.set(cacheKey, {
          ...cachedPath,
          modifiedPath,
        });

        if (targetObjectData) {
          modifiedPath.push({
            data: {
              position: {
                x: targetObjectData.positionX,
                y: targetObjectData.positionY,
                z: targetObjectData.positionZ,
              },
            },
          });
        }

        return { path: modifiedPath, hasTeleports: cachedPath.hasTeleports };
      } else {
        this.resetPathCache();
      }

    }

    // Make new path request
    let res;
    const maxRetries = 3;
    const retryDelay = 500;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        res = await this.requestFn(
          transformGetPathSweeps(pathCalculationSweeps),
          pathStartSID,
          pathEndSID,
          this.tourId,
          navigationType,
        );
        break;
      } catch (error) {
        console.error(`Path request attempt ${attempt} failed:`, error);

        if (attempt === maxRetries) {
          console.error('All path request attempts failed, returning empty path');
          return { path: [], hasTeleports: false };
        }

        await new Promise((resolve) => setTimeout(resolve, retryDelay * attempt));
      }
    }

    if (!res || !res.path) {
      console.error('Invalid path response, returning empty path');
      return { path: [], hasTeleports: false };
    }

    const path = transformPathLineInputPath(res.path, pathCalculationSweeps);

    // Cache the new path
    this.pathCache.set(cacheKey, {
      originalPath: [...path],
      modifiedPath: [...path],
      hasTeleports: res.hasTeleports,
    });

    if (targetObjectData) {
      path.push({
        data: {
          position: {
            x: targetObjectData.positionX,
            y: targetObjectData.positionY,
            z: targetObjectData.positionZ,
          },
        },
      });
    }

    return { path, hasTeleports: res.hasTeleports };
  }

  resetPathCache(endSID?: string, navigationType?: NavigationType): void {
    if (endSID && navigationType) {
      const cacheKey = this.generateCacheKey(endSID, navigationType);
      const cachedPath = this.pathCache.get(cacheKey);
      if (cachedPath) {
        // Reset modified path to original path
        this.pathCache.set(cacheKey, {
          ...cachedPath,
          modifiedPath: [...cachedPath.originalPath],
        });
      }
    } else {
      // Reset all cached paths
      this.pathCache.forEach((cache, key) => {
        this.pathCache.set(key, {
          ...cache,
          modifiedPath: [...cache.originalPath],
        });
      });
    }
  }

  clearPathCache(): void {
    this.pathCache.clear();
  }
}
